import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import {
  CalendarCheck2, Plus, RefreshCw, ChevronRight, AlertTriangle,
  CheckCircle, XCircle, Clock, Download, Filter,
} from "lucide-react";

const CATEGORY_LABELS = {
  permiso_sanitario: "Permiso sanitario",
  certificacion: "Certificacion",
  contrato_alquiler: "Contrato alquiler",
  contrato_empleado: "Contrato empleado",
  contrato_proveedor: "Contrato proveedor",
  producto_camara: "Producto en camara",
  marca: "Marca",  // Fix WH63 (2026-09-28)
  accesos_contables: "Accesos contables",  // Fix WH78 (2026-10-15)
  custom: "Personalizado",
};

const STATUS_CONFIG = {
  vigente: { label: "Vigente", color: "bg-green-100 text-green-800", Icon: CheckCircle },
  por_vencer: { label: "Por vencer", color: "bg-yellow-100 text-yellow-800", Icon: AlertTriangle },
  vencido: { label: "Vencido", color: "bg-red-100 text-red-800", Icon: XCircle },
  renovado: { label: "Renovado", color: "bg-blue-100 text-blue-800", Icon: CheckCircle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "bg-slate-100 text-slate-600", Icon: Clock };
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
}

export default function OpsVigencias() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vigencias, setVigencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [branches, setBranches] = useState([]);
  const [filterBranch, setFilterBranch] = useState("");
  // Fix WH35 (2026-09-08): filtro por razon social.
  const [entities, setEntities] = useState([]);
  const [filterEntity, setFilterEntity] = useState("");
  const [view, setView] = useState("list"); // "list" | "calendar"
  const [calendarData, setCalendarData] = useState({});
  const [calMonth, setCalMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (filterCategory) params.set("category", filterCategory);
      if (filterBranch) params.set("branch_id", filterBranch);
      if (filterEntity) params.set("legal_entity_id", filterEntity);   // Fix WH35
      const [vigRes, brRes, enRes] = await Promise.all([
        api.get(`/ops/vigencias?${params}`),
        api.get("/branches"),
        api.get("/ops/legal-entities").catch(() => ({ data: [] })),
      ]);
      setVigencias(vigRes.data || []);
      setBranches(brRes.data || []);
      setEntities(enRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterStatus, filterCategory, filterBranch, filterEntity]);

  const fetchCalendar = useCallback(async () => {
    try {
      const params = new URLSearchParams({ month: calMonth });
      if (filterBranch) params.set("branch_id", filterBranch);
      if (filterEntity) params.set("legal_entity_id", filterEntity);   // Fix WH35 (2026-09-08)
      const res = await api.get(`/ops/vigencias/calendar?${params}`);
      setCalendarData(res.data?.calendar || {});
    } catch (e) { console.error(e); }
  }, [calMonth, filterBranch, filterEntity]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (view === "calendar") fetchCalendar(); }, [view, fetchCalendar]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filterBranch) params.set("branch_id", filterBranch);
      if (filterCategory) params.set("category", filterCategory);
      const resp = await api.get(`/ops/vigencias/export.xlsx?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = "vigencias.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  // Calendar view
  const calYear = parseInt(calMonth.split("-")[0]);
  const calMonthNum = parseInt(calMonth.split("-")[1]) - 1;
  const firstDay = new Date(calYear, calMonthNum, 1);
  const daysInMonth = new Date(calYear, calMonthNum + 1, 0).getDate();
  const startDow = firstDay.getDay();
  const calDays = Array.from({ length: 42 }, (_, i) => {
    const day = i - startDow + 1;
    return (day >= 1 && day <= daysInMonth) ? day : null;
  });

  const dotColor = (status) => {
    if (status === "vencido") return "bg-red-500";
    if (status === "por_vencer") return "bg-yellow-500";
    return "bg-green-500";
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="ops-vigencias-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarCheck2 className="w-6 h-6 text-yellow-500" /> Vigencias
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Permisos, certificaciones, contratos y documentos con vencimiento</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate("/ops/vigencias/nuevo")}
            className="flex items-center gap-2 px-4 py-2 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400 transition-colors"
            data-testid="btn-crear-vigencia">
            <Plus className="w-4 h-4" /> Nueva vigencia
          </button>
          <button onClick={handleExport} className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" title="Exportar Excel">
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Vigentes", status: "vigente", color: "green" },
          { label: "Por vencer", status: "por_vencer", color: "yellow" },
          { label: "Vencidas", status: "vencido", color: "red" },
        ].map(({ label, status, color }) => {
          const count = vigencias.filter(v => v.status === status).length;
          return (
            <div key={status} className={`bg-${color}-50 border border-${color}-200 rounded-xl p-4 text-center cursor-pointer hover:bg-${color}-100 transition-colors`}
              onClick={() => setFilterStatus(filterStatus === status ? "" : status)}>
              <div className={`text-2xl font-bold text-${color}-700`}>{count}</div>
              <div className={`text-xs text-${color}-600 font-medium`}>{label}</div>
            </div>
          );
        })}
      </div>

      {/* Filters + view toggle */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap">
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white">
            <option value="">Todas las categorias</option>
            {Object.entries(CATEGORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={filterBranch} onChange={e => setFilterBranch(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white">
            <option value="">Todas las sucursales</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          {/* Fix WH35 (2026-09-08): el grupo es multi-empresa; la abogada
              necesita ver los documentos de una razon social sin el ruido de
              las otras. */}
          {entities.length > 0 && (
            <select value={filterEntity} onChange={e => setFilterEntity(e.target.value)}
              data-testid="filtro-empresa"
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white">
              <option value="">Todas las empresas</option>
              {entities.map(en => <option key={en.id} value={en.id}>{en.legal_name}</option>)}
            </select>
          )}
        </div>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden text-sm">
          {[["list", "Lista"], ["calendar", "Calendario"]].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)}
              className={`px-3 py-1.5 transition-colors ${view === v ? "bg-[#FFDF00] font-medium" : "hover:bg-slate-50 text-slate-600"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : view === "calendar" ? (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => {
              const d = new Date(calYear, calMonthNum - 1, 1);
              setCalMonth(d.toISOString().slice(0, 7));
            }} className="p-1.5 rounded-lg hover:bg-slate-100">&lt;</button>
            <span className="font-semibold text-slate-900">
              {new Date(calYear, calMonthNum).toLocaleDateString("es-MX", { month: "long", year: "numeric" })}
            </span>
            <button onClick={() => {
              const d = new Date(calYear, calMonthNum + 1, 1);
              setCalMonth(d.toISOString().slice(0, 7));
            }} className="p-1.5 rounded-lg hover:bg-slate-100">&gt;</button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center mb-2">
            {["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"].map(d => (
              <div key={d} className="text-xs font-semibold text-slate-500 py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {calDays.map((day, i) => {
              const dateStr = day ? `${calMonth}-${String(day).padStart(2, "0")}` : null;
              const items = dateStr ? (calendarData[dateStr] || []) : [];
              return (
                <div key={i} className={`min-h-[60px] rounded-lg p-1 text-xs ${day ? "bg-slate-50 hover:bg-slate-100 cursor-pointer" : ""}`}
                  onClick={() => { if (items.length > 0) navigate(`/ops/vigencias?date=${dateStr}`); }}
                >
                  {day && (
                    <>
                      <span className={`font-medium ${items.length > 0 ? "text-slate-900" : "text-slate-400"}`}>{day}</span>
                      <div className="flex flex-wrap gap-0.5 mt-0.5">
                        {items.slice(0, 3).map((item, j) => (
                          <span key={j} className={`w-1.5 h-1.5 rounded-full ${dotColor(item.status)}`} title={item.name}></span>
                        ))}
                        {items.length > 3 && <span className="text-slate-400 text-[9px]">+{items.length - 3}</span>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-full"></span>Vigente</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-yellow-500 rounded-full"></span>Por vencer</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-full"></span>Vencido</span>
          </div>
        </div>
      ) : vigencias.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <CalendarCheck2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="font-semibold text-slate-700">Todavia no hay vigencias cargadas</p>
          <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">
            Comenzá creando la primera. Recomendamos: permisos sanitarios por sucursal, alquileres, certificaciones del personal, contratos con proveedores criticos.
          </p>
          <button onClick={() => navigate("/ops/vigencias/nuevo")}
            className="mt-4 px-6 py-2.5 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400 transition-colors">
            Crear primera vigencia
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {vigencias.map(v => {
            const days = daysUntil(v.due_date);
            return (
              <div key={v.id}
                onClick={() => navigate(`/ops/vigencias/${v.id}`)}
                className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors"
                data-testid={`vigencia-row-${v.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-2 h-10 rounded-full ${v.status === "vencido" ? "bg-red-500" : v.status === "por_vencer" ? "bg-yellow-500" : "bg-green-500"}`}></div>
                  <div>
                    <div className="font-semibold text-slate-900">{v.name}</div>
                    <div className="text-xs text-slate-500">
                      {CATEGORY_LABELS[v.category] || v.category} {v.issuer ? `— ${v.issuer}` : ""}
                    </div>
                    {/* Fix WH35 (2026-09-08): a quien pertenece. Sin esto, en un
                        grupo multi-empresa no se sabe de que razon social es
                        cada documento sin abrirlo uno por uno. */}
                    {(v.legal_entity_name || v.branch_name) && (
                      <div className="text-xs text-slate-400 mt-0.5">
                        {v.legal_entity_name || ""}
                        {v.legal_entity_name && v.branch_name ? " · " : ""}
                        {v.branch_name || ""}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right text-sm">
                    <div className="text-slate-500 text-xs">Vencimiento</div>
                    <div className="font-medium">{v.due_date}</div>
                    {days !== null && (
                      <div className={`text-xs ${days < 0 ? "text-red-600" : days <= 7 ? "text-yellow-600" : "text-slate-400"}`}>
                        {days < 0 ? `Vencio hace ${Math.abs(days)} dias` : days === 0 ? "Vence hoy" : `${days} dias restantes`}
                      </div>
                    )}
                  </div>
                  <StatusBadge status={v.status} />
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
